import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AnalyticsProps } from 'support/helpers/analytics/analytics';
import { Button, ButtonType } from 'components/Form/Button/Button';

import { MenuContext, MenuContextValue, MenuListContext } from './contexts';

type MenuButtonProps = {
  className?: string;
  children: ReactNode;
  text?: ReactNode;
  Icon?: ButtonType['LeftIcon'];
  iconOnly?: ButtonType['iconOnly'];
  variant?: ButtonType['variant'];
  testId?: string;
  size?: ButtonType['size'];
  'aria-label'?: string;
} & AnalyticsProps;

export const MenuButton: FC<MenuButtonProps> = (props) => {
  const parentId = useFloatingParentNodeId();
  if (parentId === null) {
    return (
      <FloatingTree>
        <MenuComponent {...props} />
      </FloatingTree>
    );
  }
  return <MenuComponent {...props} />;
};

const MenuComponent: FC<MenuButtonProps> = ({
  size = 'small',
  className,
  children,
  text,
  variant,
  testId,
  Icon,
  iconOnly,
  analyticsId,
  analyticsProperties,
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hasFocusInside, setHasFocusInside] = useState(false);

  const listItemsRef = useRef<Array<HTMLElement | null>>([]);
  const listContentRef = useRef<Array<string | null>>([]);

  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { bubbles: true });
  const role = useRole(context, { role: 'menu' });
  const listNavigation = useListNavigation(context, {
    listRef: listItemsRef,
    activeIndex,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    onMatch: isOpen ? setActiveIndex : undefined,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNavigation,
    typeahead,
  ]);

  useEffect(() => {
    if (!tree) {
      return;
    }
    const handleTreeClose = () => setIsOpen(false);
    tree.events.on('close', handleTreeClose);
    return () => {
      tree.events.off('close', handleTreeClose);
    };
  }, [tree]);

  const menuContextValue = useMemo<MenuContextValue>(
    () => ({
      getItemProps,
      activeIndex,
      setActiveIndex,
      setHasFocusInside,
      isOpen,
      size,
    }),
    [getItemProps, activeIndex, isOpen, size],
  );

  return (
    <FloatingNode id={nodeId}>
      <Button
        RightIcon={iconOnly ? undefined : ChevronDownIcon}
        variant={variant}
        testId={testId}
        LeftIcon={Icon}
        iconOnly={iconOnly}
        className={className}
        size={size}
        analyticsId={analyticsId}
        analyticsProperties={analyticsProperties}
        aria-label={ariaLabel}
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: (event) => {
            event.stopPropagation();
          },
        })}
      >
        {text}
      </Button>
      <MenuListContext.Provider value={{ listItemsRef, listContentRef, hasFocusInside, setHasFocusInside }}>
        <MenuContext.Provider value={menuContextValue}>
          {isOpen && (
            <FloatingPortal>
              <FloatingFocusManager context={context} modal={false} initialFocus={-1} returnFocus>
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className="z-60 w-fit overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus-visible:outline-hidden"
                  {...getFloatingProps()}
                >
                  {children}
                </div>
              </FloatingFocusManager>
            </FloatingPortal>
          )}
        </MenuContext.Provider>
      </MenuListContext.Provider>
    </FloatingNode>
  );
};
